import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/SectionHeading";
import Disclaimer from "@/components/Disclaimer";
import api from "@/lib/api";
import { asArray, asStringArray, formatCount, formatCr } from "@/lib/safeData";
import {
  ShieldAlert, Eye, FileSearch, Lock, ChevronRight,
  Send, BadgeCheck, KeyRound, TrendingUp, MessageSquare,
  Image as ImageIcon, RefreshCcw, BookOpen, ExternalLink,
  Flame, Skull, AlertTriangle, Sparkles, Building2, Scale,
  Landmark, HeartPulse, Plane, ShoppingBag, PiggyBank, Home as HomeIcon, HelpCircle,
} from "lucide-react";

const HERO_BG = "/assets/hero-bg.png";
const LOGO_URL = "/assets/logo.png";
const RED_BG = "/assets/red-bg.png";

const iconMap = {
  Send, BadgeCheck, KeyRound, TrendingUp, MessageSquare,
  Image: ImageIcon, RefreshCcw, BookOpen,
};

const DEFAULT_STATS = {
  reports_verified: 5234,
  scammers_tracked: 312,
  cases_resolved: 1247,
  total_loss_reported_inr: 184500000,
};

const Hero = () => {
  const { t, lang } = useT();
  const [taglineIdx, setTaglineIdx] = useState(0);
  const taglines = asStringArray(t.hero?.taglines).length
    ? asStringArray(t.hero.taglines)
    : [t.hero?.subtitle || ""];

  useEffect(() => {
    if (taglines.length < 2) return undefined;
    const id = setInterval(() => setTaglineIdx((i) => (i + 1) % taglines.length), 3500);
    return () => clearInterval(id);
  }, [taglines.length]);

  return (
    <section className="relative overflow-hidden border-b border-white/10" data-testid="hero">
      <div className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#0B0B0B]" />
        <div className="absolute inset-0 scan-lines opacity-40" />
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-8 pt-24 pb-20 md:pt-32 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-3 py-1 mb-6">
            <span className="h-2 w-2 rounded-full bg-[#D4AF37] pulse-gold" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-mono-alt">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className={`font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight ${lang === "hi" ? "lang-hi" : ""}`}>
            <span className="block text-white">{t.hero.title1}</span>
            <span className="block">
              <span className="shimmer-text">{t.hero.title2}</span>
            </span>
          </h1>

          <p className={`mt-6 max-w-2xl text-lg text-white/70 leading-relaxed ${lang === "hi" ? "lang-hi" : ""}`}>
            {t.hero.subtitle}
          </p>

          <div className={`mt-3 h-7 font-mono-alt text-sm text-[#D4AF37]/90 transition-opacity ${lang === "hi" ? "lang-hi" : ""}`}>
            <span key={taglineIdx} className="fade-up inline-block">{taglines[taglineIdx]}</span>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/#loss-categories">
              <Button className="bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold gap-2 px-6 h-12" data-testid="hero-report-btn">
                <ShieldAlert className="h-4 w-4" />
                {t.hero.reportBtn}
              </Button>
            </Link>
            <Link to="/recovery">
              <Button variant="outline" className="border-white/25 hover:bg-white/5 text-white font-semibold gap-2 px-6 h-12" data-testid="hero-recovery-btn">
                {t.hero.recoveryBtn}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/55">
            <span className="inline-flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-[#D4AF37]" /> {t.common.confidential}</span>
            <span className="inline-flex items-center gap-2"><FileSearch className="h-3.5 w-3.5 text-[#D4AF37]" /> Evidence-Based</span>
            <span className="inline-flex items-center gap-2"><Scale className="h-3.5 w-3.5 text-[#D4AF37]" /> Official Channels</span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="surface-card grain relative overflow-hidden p-6 md:p-8" data-testid="hero-card">
            <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-10 h-60 w-60 rounded-full bg-[#FF3B30]/10 blur-3xl" />
            <div className="relative flex items-center justify-center">
              <div className="rounded-full bg-black/60 ring-1 ring-[#D4AF37]/30 p-1.5 pulse-gold">
                <img src={LOGO_URL} alt="Eagle Shield Eye" className="h-32 w-32 rounded-full object-cover" />
              </div>
            </div>
            <div className="relative mt-6 text-center">
              <div className="font-display text-2xl font-black tracking-tight">
                CHINTA<span className="text-[#D4AF37]">MAT</span>
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.24em] text-white/45 font-mono-alt">
                Vigilance · Protection · Truth
              </div>
            </div>
            <div className="relative mt-6 grid grid-cols-3 divide-x divide-white/10 text-center">
              <div className="px-2"><Eye className="h-5 w-5 text-[#D4AF37] mx-auto" /><div className="mt-1.5 text-[11px] text-white/60">Investigate</div></div>
              <div className="px-2"><ShieldAlert className="h-5 w-5 text-[#D4AF37] mx-auto" /><div className="mt-1.5 text-[11px] text-white/60">Expose</div></div>
              <div className="px-2"><Scale className="h-5 w-5 text-[#D4AF37] mx-auto" /><div className="mt-1.5 text-[11px] text-white/60">Recover</div></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const lossIconMap = {
  trading: TrendingUp,
  banking: Landmark,
  cyber: ShieldAlert,
  insurance: HeartPulse,
  airline: Plane,
  consumer: ShoppingBag,
  housing: HomeIcon,
  pension: PiggyBank,
  other: HelpCircle,
};

const lossImageMap = {
  trading: "/assets/loss-categories/loss-trading.jpg",
  banking: "/assets/loss-categories/loss-banking.jpg",
  cyber: "/assets/loss-categories/loss-cyber.jpg",
  insurance: "/assets/loss-categories/loss-insurance.jpg",
  airline: "/assets/loss-categories/loss-airline.jpg",
  consumer: "/assets/loss-categories/loss-consumer.jpg",
  housing: "/assets/loss-categories/loss-housing.jpg",
  pension: "/assets/loss-categories/loss-pension.jpg",
  other: "/assets/loss-categories/loss-cyber.jpg",
};

const LossCategories = () => {
  const { t, lang } = useT();
  const lc = t.lossCategories || {};
  const items = asArray(lc.items);
  if (!items.length) return null;
  return (
    <section className="section-pad border-b border-white/10" id="loss-categories" data-testid="loss-categories-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading eyebrow="01 · Choose Your Path" title={lc.title} subtitle={lc.subtitle} />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((c, i) => {
            const Icon = lossIconMap[c.key] || ShieldAlert;
            const imageSrc = lossImageMap[c.key];
            return (
              <Link
                key={c.key ?? `loss-${i}`}
                to={`/loss/${c.key}`}
                className="surface-card group relative overflow-hidden block"
                data-testid={`loss-cat-${c.key ?? i}`}
              >
                <div className="relative h-36 overflow-hidden">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-[#1a1a1a] to-[#0B0B0B]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-black/40 to-black/10" />
                </div>
                <div className="relative p-5 pt-0">
                  <div className="relative -mt-5 mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0B0B0B] text-[#D4AF37] ring-2 ring-[#D4AF37]/30 shadow-lg">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className={`font-display text-lg font-bold leading-snug ${lang === "hi" ? "lang-hi" : ""}`}>{c.title}</div>
                  <div className={`mt-2 text-sm text-white/60 leading-relaxed line-clamp-3 ${lang === "hi" ? "lang-hi" : ""}`}>{c.desc}</div>
                  <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-alt">{lc.authorityLabel}</div>
                      <div className="text-[11px] text-[#D4AF37]/90 font-mono-alt truncate">{c.authority}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/40 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const StatsBar = () => {
  const { t } = useT();
  const [stats, setStats] = useState(DEFAULT_STATS);
  useEffect(() => {
    api.get("/stats")
      .then((r) => setStats({ ...DEFAULT_STATS, ...(r.data || {}) }))
      .catch(() => {});
  }, []);
  const items = [
    { label: t.stats.reports, value: `${formatCount(stats.reports_verified)}+`, icon: FileSearch },
    { label: t.stats.scammers, value: `${formatCount(stats.scammers_tracked)}+`, icon: Skull },
    { label: t.stats.cases, value: `${formatCount(stats.cases_resolved)}+`, icon: Scale },
    { label: t.stats.loss, value: formatCr(stats.total_loss_reported_inr), icon: Building2 },
  ];
  return (
    <section className="border-b border-white/10 bg-black/40" data-testid="stats-bar">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map(({ label, value, icon: Icon }) => (
          <div key={label} className="flex items-center gap-4">
            <div className="h-11 w-11 grid place-items-center rounded-lg bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]/20">
              <Icon className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">{value}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-mono-alt">{label}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Trust = () => {
  const { t, lang } = useT();
  return (
    <section className="section-pad" data-testid="trust-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading eyebrow="02 · Foundations" title={t.trust.title} />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.trust.items.map((it, i) => (
            <div key={i} className="surface-card p-6 group" data-testid={`trust-card-${i}`}>
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] ring-1 ring-[#D4AF37]/20">
                {i === 0 ? <Lock className="h-5 w-5" /> :
                 i === 1 ? <FileSearch className="h-5 w-5" /> :
                 i === 2 ? <Sparkles className="h-5 w-5" /> :
                           <ShieldAlert className="h-5 w-5" />}
              </div>
              <div className={`mt-5 font-display text-lg font-bold ${lang === "hi" ? "lang-hi" : ""}`}>{it.title}</div>
              <div className={`mt-2 text-sm text-white/60 leading-relaxed ${lang === "hi" ? "lang-hi" : ""}`}>{it.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CommonScams = () => {
  const { t, lang } = useT();
  return (
    <section className="section-pad border-t border-white/5" data-testid="scams-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading eyebrow="03 · Pattern Library" title={t.scams.title} subtitle={t.scams.subtitle} />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.scams.categories.map((c, i) => {
            const Icon = iconMap[c.icon] || ShieldAlert;
            return (
              <div key={c.key} className="surface-card p-6 group relative overflow-hidden" data-testid={`scam-card-${c.key}`}>
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[#FF3B30]/5 blur-2xl group-hover:bg-[#FF3B30]/20 transition-colors" />
                <div className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#FF3B30]/10 text-[#FF3B30] ring-1 ring-[#FF3B30]/20">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="relative mt-5 font-mono-alt text-[10px] uppercase tracking-widest text-white/40">#{(i + 1).toString().padStart(2, "0")}</div>
                <div className={`relative mt-1 font-display text-lg font-bold ${lang === "hi" ? "lang-hi" : ""}`}>{c.title}</div>
                <div className={`relative mt-2 text-sm text-white/60 leading-relaxed ${lang === "hi" ? "lang-hi" : ""}`}>{c.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Recovery = () => {
  const { t, lang } = useT();
  return (
    <section className="section-pad border-t border-white/5 relative" data-testid="recovery-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading eyebrow="04 · Path to Resolution" title={t.recovery.title} subtitle={t.recovery.subtitle} />

        <div className="mt-14 relative">
          <div className="absolute left-5 md:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-[#D4AF37] via-white/15 to-transparent md:-translate-x-1/2" />
          <div className="space-y-8">
            {t.recovery.steps.map((s, i) => (
              <div key={i} className={`relative md:grid md:grid-cols-2 md:gap-12 ${i % 2 === 0 ? "" : "md:[direction:rtl]"}`}>
                <div className={`${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12 md:[direction:ltr]"}`}>
                  <div className="surface-card p-6 ml-12 md:ml-0" data-testid={`recovery-step-${i + 1}`}>
                    <div className="font-mono-alt text-xs text-[#D4AF37]">STEP {String(i + 1).padStart(2, "0")}</div>
                    <div className={`mt-1 font-display text-xl font-bold ${lang === "hi" ? "lang-hi" : ""}`}>{s.title}</div>
                    <div className={`mt-2 text-sm text-white/60 leading-relaxed ${lang === "hi" ? "lang-hi" : ""}`}>{s.desc}</div>
                  </div>
                </div>
                <div className={`${i % 2 === 0 ? "" : "md:[direction:ltr]"}`}></div>
                <div className="absolute left-5 md:left-1/2 top-6 -translate-x-1/2 h-3 w-3 rounded-full bg-[#D4AF37] ring-4 ring-[#0B0B0B]" />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 max-w-2xl">
          <Disclaimer text={t.recovery.disclaimer} variant="alert" />
        </div>
      </div>
    </section>
  );
};

const OfficialLinks = () => {
  const { t, lang } = useT();
  return (
    <section className="section-pad border-t border-white/5" data-testid="official-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading eyebrow="05 · Government Portals" title={t.official.title} subtitle={t.official.subtitle} />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.official.items.map((it, i) => (
            <a
              key={it.url}
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              className="surface-card tracing-border p-6 block relative"
              data-testid={`official-link-${i}`}
            >
              <div className="flex items-start justify-between">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] ring-1 ring-[#D4AF37]/30">
                  <Building2 className="h-5 w-5" />
                </div>
                <ExternalLink className="h-4 w-4 text-white/40" />
              </div>
              <div className={`mt-5 font-display text-lg font-bold ${lang === "hi" ? "lang-hi" : ""}`}>{it.title}</div>
              <div className={`mt-2 text-sm text-white/60 leading-relaxed ${lang === "hi" ? "lang-hi" : ""}`}>{it.desc}</div>
              <div className="mt-4 font-mono-alt text-[11px] text-[#D4AF37]/80 truncate">{it.url.replace("https://", "")}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReportCTA = () => {
  const { t, lang } = useT();
  return (
    <section className="section-pad border-t border-white/5 relative overflow-hidden" data-testid="report-cta">
      <img src={RED_BG} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black via-black/80 to-black/60" />
      <div className="mx-auto max-w-5xl px-4 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-3 py-1 mb-5">
          <Flame className="h-3.5 w-3.5 text-[#FF3B30]" />
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#FF3B30] font-mono-alt">Act Now</span>
        </div>
        <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight ${lang === "hi" ? "lang-hi" : ""}`}>
          {lang === "hi" ? "अपनी चुप्पी ही उनकी ताकत है।" : "Your silence is their strength."}
        </h2>
        <p className={`mt-4 max-w-2xl mx-auto text-white/70 ${lang === "hi" ? "lang-hi" : ""}`}>
          {lang === "hi"
            ? "एक रिपोर्ट दूसरों को बचा सकती है। आपकी पहचान हमेशा गोपनीय रहती है।"
            : "One report can save another victim. Your identity stays strictly confidential."}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/report">
            <Button className="bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold gap-2 px-6 h-12" data-testid="cta-report-btn">
              <ShieldAlert className="h-4 w-4" />
              {t.hero.reportBtn}
            </Button>
          </Link>
          <Link to="/risk-checker">
            <Button variant="outline" className="border-white/30 hover:bg-white/5 text-white font-semibold gap-2 px-6 h-12" data-testid="cta-risk-btn">
              {t.nav.risk}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const Stories = () => {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/stories")
      .then((r) => setItems(asArray(r.data).slice(0, 3)))
      .catch(() => setItems([]));
  }, []);
  const storyList = asArray(items);
  if (!storyList.length) return null;
  return (
    <section className="section-pad border-t border-white/5" data-testid="stories-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <SectionHeading eyebrow="07 · Voices" title={t.stories.title} subtitle={t.stories.subtitle} />
          <Link to="/stories" className="text-sm text-[#D4AF37] hover:underline" data-testid="all-stories-link">
            {t.common.readMore} →
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {storyList.map((s, i) => (
            <div key={s.id ?? `story-${i}`} className="surface-card p-6" data-testid={`story-card-${s.id ?? i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">{s.scam_type}</span>
                <span className="text-[10px] text-white/40 font-mono-alt">{s.state}</span>
              </div>
              <div className={`mt-3 font-display text-lg font-bold leading-snug ${lang === "hi" ? "lang-hi" : ""}`}>{s.title}</div>
              <div className="mt-3 text-sm text-white/65 leading-relaxed line-clamp-4">{s.summary}</div>
              <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-alt">{t.stories.lossLabel}</div>
                  <div className="text-[#FF6A60] font-bold font-mono-alt">{formatCr(s.amount_lost)}</div>
                </div>
                <AlertTriangle className="h-5 w-5 text-[#FF3B30]/70" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div data-testid="home-page">
      <Hero />
      <LossCategories />
      <StatsBar />
      <Trust />
      <CommonScams />
      <Recovery />
      <OfficialLinks />
      <ReportCTA />
      <Stories />
    </div>
  );
};

export default Home;
